// bookingTimeSlot.utils.ts
import isEmpty from "lodash/isEmpty";
import moment from "moment";

import { BOOKING_CONSTANTS } from "@constants/Bookings";
import { DateFormats } from "@enums/shared.enum";
import {
  BookingFetchResponseType,
  ResourceSlotData,
  ResourceType,
  timeMapObj,
} from "@models/services/booking";

import {
  normalizeSlotEnd,
  policyLateBookingWindowMinutes,
} from "./booking.utils";
import { toMinutes } from "./common.utils";

export const buildTimeSlotForResource = (
  slots: BookingFetchResponseType,
  resourceId: string,
  selectedDate: string
) => {
  if (isEmpty(slots) || !slots?.resources?.length) {
    return [];
  }

  // Find the specific resource
  const resource = slots.resources.find(r => r.id === resourceId);

  if (!resource?.slotInfo) {
    return [];
  }
  const isToday = selectedDate === moment().format(DateFormats.YMD);
  const lateBookingWindow = isToday
    ? policyLateBookingWindowMinutes(resource?.policyInfo)
    : 0;

  let startTime = moment(resource.slotInfo.slotStart).subtract(
    lateBookingWindow,
    "minutes"
  );
  const endTime = moment(normalizeSlotEnd(resource.slotInfo.slotEnd));
  const slotTime = slots?.slotSplit ?? BOOKING_CONSTANTS.WIDTH_PER_SLOT;

  const allowedTimeSlots = resource.policyInfo?.bookingStartTimes || [];
  const availableSlots = resource.slotInfo?.slots?.availableSlots || [];
  const occupiedSlots = resource.slotInfo?.slots?.occupiedSlots || [];

  if (moment(selectedDate, DateFormats.YMD).isBefore(moment(), "day")) {
    return [];
  }

  if (startTime.isValid()) {
    const dayStart = moment(selectedDate).startOf("day");
    startTime = moment.max(startTime, dayStart);
  }

  // Adjust start time if booking for today
  if (
    selectedDate === moment().format(DateFormats.YMD) &&
    startTime.isValid()
  ) {
    const referenceTime = moment().subtract(lateBookingWindow, "minutes");
    startTime = getNextTimeMark(startTime, slotTime, referenceTime);
  }

  if (!startTime.isValid() || availableSlots.length === 0) {
    return [];
  }

  const dayEnd = moment(selectedDate).endOf("day");
  const effectiveEndTime = moment.min(endTime, dayEnd);

  const applicableTimeSlots = generateTimeIntervals(
    startTime,
    effectiveEndTime,
    slotTime
  );

  // Get allowed slots from this resource's allowed durations
  const allowedSlots: string[] = Object.keys(
    resource.slotInfo?.slots?.allowedDurations || {}
  ).map(key => key.replace("T_", ""));

  const notAllowedSlots = applicableTimeSlots.filter(
    time => !allowedSlots.includes(time)
  );

  const updatedOccupiedSlots = [...occupiedSlots, ...notAllowedSlots];

  const slotData = {
    availableSlots,
    occupiedSlots: updatedOccupiedSlots,
    unavailableSlots: applicableTimeSlots.filter(
      time =>
        !availableSlots.includes(time) || updatedOccupiedSlots.includes(time)
    ),
  };

  const timeSlotOptions = setTimeSlotSelectionForResource(
    applicableTimeSlots,
    slotData,
    allowedTimeSlots,
    resource,
    selectedDate
  );

  return timeSlotOptions;
};

const setTimeSlotSelectionForResource = (
  applicableTimeSlots: string[],
  slotData: ResourceSlotData,
  allowedTimeSlots: string[],
  resource: ResourceType,
  selectedDate: string
): Array<timeMapObj[]> => {
  const groupedByHour: Record<string, timeMapObj[]> = {};

  applicableTimeSlots.forEach(time => {
    const [hourStr, minuteStr] = time.split(":");

    if (
      (allowedTimeSlots.includes(minuteStr) ||
        (minuteStr === "00" && allowedTimeSlots.includes("60"))) &&
      slotData.availableSlots.includes(time)
    ) {
      const slotDateTime = moment(
        `${selectedDate}T${time}`,
        DateFormats.YMDTime
      );
      if (slotDateTime.isBefore(moment())) {
        const isToday = selectedDate === moment().format(DateFormats.YMD);
        const lateBookingWindow = isToday
          ? policyLateBookingWindowMinutes(resource?.policyInfo)
          : 0;
        const lateBookingLimit = slotDateTime
          .clone()
          .add(lateBookingWindow, "minutes");
        if (moment().isAfter(lateBookingLimit)) {
          return;
        }
      }
      const allowed = resource.slotInfo?.slots?.allowedDurations;
      const key = `T_${time}`;

      const durations =
        allowed?.[key] && typeof allowed[key] === "object"
          ? Object.keys(allowed[key]).map(k =>
              parseInt(k.replace("T_", ""), 10)
            )
          : [];

      if (durations.length === 0) return;

      const minDuration = Math.min(...durations);
      const requiredSlots = minDuration / BOOKING_CONSTANTS.WIDTH_PER_SLOT;

      let hour = Number(hourStr);
      let minute = Number(minuteStr);
      let allSlotsFree = true;

      for (let i = 0; i < requiredSlots; i++) {
        const timeStr = `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;

        if (
          !slotData.availableSlots.includes(timeStr) ||
          slotData.occupiedSlots.includes(timeStr)
        ) {
          allSlotsFree = false;
          break;
        }

        minute += BOOKING_CONSTANTS.WIDTH_PER_SLOT;
        if (minute >= BOOKING_CONSTANTS.MINS_IN_HOUR) {
          hour += Math.floor(minute / BOOKING_CONSTANTS.MINS_IN_HOUR);
          minute %= BOOKING_CONSTANTS.MINS_IN_HOUR;
        }
      }

      const slot: timeMapObj = { disabled: !allSlotsFree, time };

      if (!groupedByHour[hourStr]) groupedByHour[hourStr] = [];
      groupedByHour[hourStr].push(slot);
    }
  });

  return Object.keys(groupedByHour)
    .sort((a, b) => Number(a) - Number(b))
    .map(hour => groupedByHour[hour]);
};

export const getNextTimeMark = (
  targetTime: moment.Moment,
  interval: number,
  referenceTime: moment.Moment = moment()
): moment.Moment => {
  const currentTime = referenceTime;

  if (targetTime.isBefore(currentTime, DateFormats.Minutes)) {
    targetTime
      .year(currentTime.year())
      .month(currentTime.month())
      .date(currentTime.date())
      .hour(currentTime.hour())
      .minute(currentTime.minute());
  }

  const remainder = targetTime.minute() % interval;
  if (remainder !== 0) {
    targetTime.add(interval - remainder, DateFormats.Minutes);
  }

  if (interval === BOOKING_CONSTANTS.INTERVAL_30_MINUTES) {
    targetTime.minute(
      targetTime.minute() >= BOOKING_CONSTANTS.ROUNDED_30_MINUTE_MARK
        ? BOOKING_CONSTANTS.ROUNDED_30_MINUTE_MARK
        : BOOKING_CONSTANTS.ROUNDED_0_MINUTE_MARK
    );
  } else if (
    interval === BOOKING_CONSTANTS.INTERVAL_15_MINUTES &&
    targetTime.minute() >= BOOKING_CONSTANTS.MINS_IN_HOUR
  ) {
    targetTime.minute(BOOKING_CONSTANTS.ROUNDED_0_MINUTE_MARK);
  }

  return targetTime;
};

export const generateTimeIntervals = (
  startTime: moment.Moment,
  endTime: moment.Moment,
  interval: number
): string[] => {
  const times: string[] = [];
  const currentTime = moment(startTime);

  while (
    currentTime.isSameOrBefore(endTime) &&
    currentTime.isSame(startTime, "day")
  ) {
    times.push(currentTime.format(DateFormats.Time));
    currentTime.add(interval, DateFormats.Minutes);
  }

  if (
    times.length > 0 &&
    moment(times[times.length - 1], DateFormats.Time).isAfter(endTime)
  ) {
    times.pop();
  }

  return Array.from(new Set(times));
};

export const finalTimeSlotsForDrawer = (
  timeMaps: Array<Array<timeMapObj>>,
  selectedTime: string,
  selectedDate: string
): Array<timeMapObj> => {
  const flatTimes = timeMaps.flat();
  if (flatTimes.length === 0 || isEmpty(selectedTime)) return [];

  const isToday = moment(selectedDate, DateFormats.YMD).isSame(
    moment(),
    DateFormats.Day
  );

  let nowMinutes = toMinutes(selectedTime);

  if (isToday && isEmpty(selectedTime)) {
    nowMinutes = toMinutes(moment().format(DateFormats.Time));
  }

  const startIndex = flatTimes.findIndex(t => {
    if (t.disabled) return false;

    if (selectedTime) {
      return toMinutes(t.time) >= toMinutes(selectedTime);
    }

    return toMinutes(t.time) > nowMinutes;
  });

  if (startIndex === -1) return [];

  return flatTimes
    .slice(startIndex)
    .filter(t => !t.disabled)
    .slice(0, 4);
};
